import React, { useContext, useEffect, useState, useCallback } from 'react';
import { SocketContext } from '../../../App';
import refresh from '../../../images/login-page/refresh.png';
import WindowLayout from '../WindowLayout/WindowLayout';
import ServersTable from './ServersTable/ServersTable';
import withLoading from '../../HOC/withLoading';
import useSocketData from '../../../hooks/useSocketData';
import PasswordField from '../../UI/PasswordField';
import styles from './JoinServer.module.css';
import { backendUrl } from '../../../config/api';

const ACTIVE_SESSION_KEY = 'ludoActiveSession';
const LAST_RESULT_KEY = 'ludoLastGameResult';

const readStoredObject = key => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        console.error(`Failed to read ${key}:`, err);
        return null;
    }
};

const writeStoredObject = (key, value) => {
    if (typeof window === 'undefined') return;
    try {
        if (value === null || value === undefined) {
            window.localStorage.removeItem(key);
        } else {
            window.localStorage.setItem(key, JSON.stringify(value));
        }
    } catch (err) {
        console.error(`Failed to write ${key}:`, err);
    }
};

const JoinServer = () => {
    const socket = useContext(SocketContext);
    const [rooms, setRooms] = useSocketData('room:rooms');
    const [password, setPassword] = useState('');
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showRunning, setShowRunning] = useState(true);
    const [activeSession, setActiveSession] = useState(() => readStoredObject(ACTIVE_SESSION_KEY));
    const [lastCompletedGame, setLastCompletedGame] = useState(() => readStoredObject(LAST_RESULT_KEY));
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [showStoredResultModal, setShowStoredResultModal] = useState(false);
    const [resumePending, setResumePending] = useState(false);
    const [resumeError, setResumeError] = useState('');

    const getRooms = useCallback(() => {
        setIsLoading(true);
        if (socket?.connected) {
            setRooms([]);
            socket.emit('room:rooms');
        }
        // When disconnected, the SSE fallback keeps the last good list and
        // replaces it as soon as the next room snapshot arrives.
    }, [socket, setRooms]);

    useEffect(() => {
        if (!socket) return; // Add check to prevent errors if socket is null
        
        try {
            const handleRoomRooms = () => {
                setIsLoading(false);
            };

            const handleRoomsUpdate = (updatedRooms) => {
                try {
                    const parsedRooms = typeof updatedRooms === 'string' ? JSON.parse(updatedRooms) : updatedRooms;
                    setRooms(parsedRooms || []);
                } catch (err) {
                    console.error("Error parsing rooms update:", err);
                    setRooms([]);
                }
            };

            const handleActiveSession = (sessionData) => {
                if (sessionData) {
                    const enrichedSession = {
                        ...sessionData,
                        updatedAt: Date.now(),
                    };
                    setActiveSession(enrichedSession);
                    writeStoredObject(ACTIVE_SESSION_KEY, enrichedSession);
                    if (sessionData.status === 'completed') {
                        const completedSnapshot = {
                            ...sessionData,
                            completedAt: Date.now(),
                        };
                        setLastCompletedGame(completedSnapshot);
                        writeStoredObject(LAST_RESULT_KEY, completedSnapshot);
                    }
                } else {
                    setActiveSession(null);
                    writeStoredObject(ACTIVE_SESSION_KEY, null);
                }
            };

            const handleGameJoined = () => {
                setResumePending(false);
                setResumeError('');
                window.location.assign('/play');
            };

            const handleReconnectFailed = payload => {
                setResumePending(false);
                setResumeError(payload?.message || 'This saved game is no longer available.');
                if (payload?.stale !== false) {
                    setActiveSession(null);
                    writeStoredObject(ACTIVE_SESSION_KEY, null);
                }
            };

            socket.emit('room:rooms');
            socket.on('room:rooms', handleRoomRooms);
            
            // Add rooms:update listener for real-time updates
            socket.on('rooms:update', handleRoomsUpdate);
            
            // Check if player has an active game session
            socket.emit('player:check-session');
            socket.on('player:active-session', handleActiveSession);
            
            // Handle reconnection notifications
            socket.on('game:joined', handleGameJoined);
            socket.on('reconnection:failed', handleReconnectFailed);

            // Auto-refresh disabled per request
            
            return () => {
                if (socket) {
                    socket.off('player:active-session', handleActiveSession);
                    socket.off('game:joined', handleGameJoined);
                    socket.off('reconnection:failed', handleReconnectFailed);
                    socket.off('rooms:update', handleRoomsUpdate);
                    socket.off('room:rooms', handleRoomRooms);
                }
                
                // No interval to clear
            };
        } catch (err) {
            console.error("Error in JoinServer effect:", err);
            // Set loading to false to prevent eternal spinner
            setIsLoading(false);
        }
    }, [socket, setRooms]);

    // Lobby fallback: gameplay stays on Socket.IO/WebSocket, while SSE keeps
    // the public room list live if WebSocket is temporarily unavailable.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.EventSource !== 'function') return undefined;

        let source = null;
        const closeSource = () => {
            if (source) source.close();
            source = null;
        };
        const openSource = () => {
            if (source || socket?.connected) return;
            source = new window.EventSource(`${backendUrl}/api/stream/rooms`);
            source.addEventListener('rooms', event => {
                try {
                    const payload = JSON.parse(event.data || '{}');
                    setRooms(Array.isArray(payload.rooms) ? payload.rooms : []);
                    setIsLoading(false);
                } catch (error) {
                    console.error('Invalid SSE room payload:', error);
                }
            });
            source.onerror = () => {
                // EventSource reconnects automatically; keep the last good room list.
            };
        };
        const handleConnect = () => closeSource();
        const handleDisconnect = () => openSource();

        if (!socket?.connected) openSource();
        socket?.on('connect', handleConnect);
        socket?.on('disconnect', handleDisconnect);

        return () => {
            socket?.off('connect', handleConnect);
            socket?.off('disconnect', handleDisconnect);
            closeSource();
        };
    }, [socket, setRooms]);

    useEffect(() => {
        if (activeSession?.status === 'completed') {
            setShowCompletionModal(true);
        } else {
            setShowCompletionModal(false);
        }
    }, [activeSession]);

    useEffect(() => {
        if (!activeSession && lastCompletedGame) {
            setShowStoredResultModal(true);
        } else {
            setShowStoredResultModal(false);
        }
    }, [activeSession, lastCompletedGame]);

    const handleJoinClick = room => {
        try {
            const playerName = localStorage.getItem('playerName');
            
            if (!playerName) {
                // Redirect to login page if somehow name is not set
                window.location.href = '/';
                return;
            }
            
            if (room && room.private) {
                setSelectedRoom(room);
            } else if (room && socket) {
                socket.emit('player:login', {
                    name: playerName,
                    roomId: room._id
                });
            }
        } catch (err) {
            console.error("Error handling join click:", err);
        }
    };
    
    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        try {
            const playerName = localStorage.getItem('playerName');
            
            socket.emit('player:login', {
                name: playerName,
                roomId: selectedRoom._id,
                password: password
            });
            
            setPassword('');
            setSelectedRoom(null);
        } catch (err) {
            console.error("Error handling password submit:", err);
        }
    };

    const toggleShowRunning = () => {
        setShowRunning(prev => !prev);
    };

    const toggleAutoRefresh = () => {
        setAutoRefresh(prev => !prev);
    };
    
    const handleReconnect = () => {
        try {
            if (!socket || resumePending) return;
            if (!activeSession?.roomId || !activeSession?.playerId || activeSession.status === 'completed') return;

            setResumePending(true);
            setResumeError('');
            socket.timeout(10000).emit(
                'player:restore-session',
                {
                    roomId: activeSession.roomId,
                    playerId: activeSession.playerId,
                    color: activeSession.color,
                    seat: activeSession.seat,
                },
                (timeoutError, response) => {
                    if (timeoutError) {
                        setResumePending(false);
                        setResumeError('Game server did not answer. Try again.');
                        return;
                    }
                    if (!response?.success) {
                        setResumePending(false);
                        setResumeError(response?.message || 'Could not restore the saved game.');
                        if (response?.stale === true) {
                            setActiveSession(null);
                            writeStoredObject(ACTIVE_SESSION_KEY, null);
                        }
                        return;
                    }
                    socket.timeout(10000).emit(
                        'player:reconnect',
                        activeSession.roomId,
                        (reconnectTimeout, reconnectResponse) => {
                            setResumePending(false);
                            if (reconnectTimeout || !reconnectResponse?.success) {
                                setResumeError(reconnectResponse?.message || 'Could not rejoin the game room.');
                                return;
                            }
                            window.location.assign('/play');
                        }
                    );
                }
            );
        } catch (err) {
            setResumePending(false);
            setResumeError('Could not resume the game.');
            console.error('Error handling reconnect:', err);
        }
    };

    const handleAbandonGame = () => {
        try {
            if (activeSession) {
                socket.emit('player:abandon-game');
                setActiveSession(null);
                writeStoredObject(ACTIVE_SESSION_KEY, null);
            }
        } catch (err) {
            console.error("Error handling abandon game:", err);
        }
    };

    const clearStoredResult = () => {
        setLastCompletedGame(null);
        setShowStoredResultModal(false);
        writeStoredObject(LAST_RESULT_KEY, null);
    };

    const ServersTableWithLoading = withLoading(ServersTable);

    const tableControls = (
        <div className={styles.tableControls}>
            <label className={styles.showRunningLabel}>
                <input
                    type="checkbox"
                    checked={showRunning}
                    onChange={toggleShowRunning}
                />
                Show Running Games
            </label>
            <label className={styles.showRunningLabel}>
                <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={toggleAutoRefresh}
                />
                Auto Refresh
            </label>
        </div>
    );
    
    const content = () => {
        if (activeSession) {
            if (activeSession.status === 'completed') {
                return (
                    <div className={styles.reconnectContainer}>
                        <div className={styles.reconnectMessage}>
                            <h3>Last game completed</h3>
                            <p>Room: {activeSession.roomName || 'Game Room'}</p>
                            <p>
                                Winner: {activeSession.winnerName || activeSession.winner || 'Unknown'}
                            </p>
                            <div className={styles.reconnectButtons}>
                                <button onClick={handleAbandonGame} className={styles.abandonButton}>
                                    Clear Session
                                </button>
                            </div>
                        </div>
                    </div>
                );
            }
            return (
                <div className={styles.reconnectContainer}>
                    <div className={styles.reconnectMessage}>
                        <h3>You have an active game!</h3>
                        <p>Room: {activeSession.roomName || 'Game Room'}</p>
                        {resumeError && <p style={{ color: '#ff7b72' }}>{resumeError}</p>}
                        <div className={styles.reconnectButtons}>
                            <button onClick={handleReconnect} className={styles.reconnectButton} disabled={resumePending}>
                                {resumePending ? 'Reconnecting…' : 'Continue Playing'}
                            </button>
                            <button onClick={handleAbandonGame} className={styles.abandonButton}>
                                Abandon Game
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
        
        if (selectedRoom) {
            return (
                <div className={styles.form}>
                    <PasswordField
                        placeholder='password'
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                    />
                    <button type='submit' onClick={handlePasswordSubmit}>
                        Join
                    </button>
                </div>
            );
        }
        
        return (
            <div className={styles.serversTableContainer}>
                {tableControls}
                <ServersTableWithLoading
                    isLoading={isLoading}
                    rooms={rooms}
                    handleJoinClick={handleJoinClick}
                    showRunning={showRunning}
                />
            </div>
        );
    };

    return (
        <>
        <WindowLayout
            title='Join A Server'
            titleComponent={
                !selectedRoom && !activeSession && (
                    <div className={styles.refresh}>
                        <img 
                            src={refresh} 
                            alt='refresh' 
                            onClick={getRooms} 
                            className={autoRefresh ? styles.autoRefreshing : ''}
                        />
                    </div>
                )
            }
            content={content()}
        />
        {showCompletionModal && activeSession?.status === 'completed' && (
            <div className={styles.completionOverlay}>
                <div className={styles.completionModal}>
                    <h3>Game Finished</h3>
                    <p>Your previous game has ended.</p>
                    <p>
                        Winner: {activeSession.winnerName || activeSession.winner || 'Unknown'}
                    </p>
                    <div className={styles.completionActions}>
                        <button onClick={() => setShowCompletionModal(false)}>Close</button>
                        <button onClick={handleAbandonGame} className={styles.abandonButton}>
                            Clear Session
                        </button>
                    </div>
                </div>
            </div>
        )}
        {showStoredResultModal && lastCompletedGame && (
            <div className={styles.completionOverlay}>
                <div className={styles.completionModal}>
                    <h3>Recent Game Summary</h3>
                    <p>Room: {lastCompletedGame.roomName || 'Game Room'}</p>
                    <p>
                        Winner: {lastCompletedGame.winnerName || lastCompletedGame.winner || 'Unknown'}
                    </p>
                    <div className={styles.completionActions}>
                        <button onClick={clearStoredResult}>Dismiss</button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default JoinServer;
